import React, { useEffect, useState } from 'react'
import PageLoader from '../../shared/PageLoader'
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { 
  Box, Button, FormControl, IconButton, 
  InputAdornment, InputLabel, MenuItem, Modal, OutlinedInput, 
  Select as MSelect, Table, TableBody, TableCell, TableContainer, TableHead, 
  TablePagination, TableRow, TableSortLabel, Typography, Chip, 
  Popover
} from '@mui/material';

import { IoMdClose, IoMdSearch } from "react-icons/io";
import Icon from '../../common/Icon';
import {  downloadWithPresignedUrl } from '../../../utils/common';
import { ERROR_MESSAGES, ESTABLISHMENT_TYPE } from '../../../utils/constants';
import { toast } from 'react-toastify';
import { hasUserAccess } from '../../../backend/auth';
import { USER_PRIVILEGES } from '../UserManagement/Roles/RoleConfiguration';
import { 
  addStateRegisterQue
} from '../../../redux/features/stateRegisterQue.slice'

import {
  useFilters,
  useSearchConfig,
  useStateRegisterData,
  useCompanyData,
  useEmployerData
} from '../../../hooks/useRegistersQue';
import {
  formatDate,
  generateYearsList,
  MONTH_LIST,
  getNameById,
  getResetFields
} from '../../../utils/registersQue.utils';
import FormRenderer, { ComponentMapper, componentTypes, FormTemplate } from '../../common/FormRenderer';

const InputsDashboard = () => {
  const dispatch: any = useAppDispatch();
  const stateRegisterQueDetails = useAppSelector((state) => state.stateRegisterQue.stateRegisterQueDetails)
  const stateRegisterQueExcelandPdfDetails = useAppSelector((state) => state.stateRegisterQue.stateRegisterQueExcelandPdfDownloadDetails)
  
  const QUE_STATUS_DISPLAY: Record<string, string> = {
    pending: 'Pending',
    processing: 'In Progress',
    excel_processing: 'In Progress',
    pdf_processing: 'In Progress',
    pdf_processing_partially_completed: 'Failed',
    pdf_processing_completed: 'Completed',
    failed: 'Failed'
  };

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters } = useFilters();
  const { searchConfig, updateSearchConfig } = useSearchConfig();
  const { fetchData, fetchWithSearch } = useStateRegisterData(filters, searchConfig);
  const {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates
  } = useCompanyData();
  const {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations
  } = useEmployerData();
  
  // Extract states from locations
  let states = (locations || []).map((each: any) => {
    const { state }: any = each.location || {};
    return { name: state.name, id: state.id }
  }).filter((each: any, index: any, self: any) => {
    return index === self.findIndex((t: any) => t.id === each.id);
  }) || [];
  
  // Local state for UI components
  const [downloading, setDownloading] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [selectedStateRegisterQue, setSelectedStateRegisterQue] = useState<any>({});
  const [stateRegisters, setStateRegisters] = useState<any[]>([]);
  const [showDashboardDetails, setShowDashboardDetails] = useState(true);
  const [queueNumbers, SetqueueNumbers] = useState<string[]>([]);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [debugForm, setDebugForm] = useState<any>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Modal styling
  const styleUploadModal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    bgcolor: 'background.paper',
    boxShadow: 24,
  };
  
  // Redux selectors
  const formsDetails = useAppSelector((state) => state.inputModule.formsDetails)
  const addStateRegisterDetails = useAppSelector((state) => state.stateRegister.addStateRegisterDetails)
  const updateStateRegisterDetails = useAppSelector((state) => state.stateRegister.updateStateRegisterDetails)
  const deleteStateRegisterMappingDetails = useAppSelector((state) => state.stateRegister.deleteStateRegisterMappingDetails)
  const importStateRegisterMappingDetails = useAppSelector((state) => state.stateRegister.importStateRegisterMappingDetails);
  
  // Data from selectors
  const stateRegisterQue = stateRegisterQueDetails.data.items
  const stateRegisterQueCount = stateRegisterQueDetails.data.meta.total_count
  const stateRegisterQueExcelandPdf = stateRegisterQueExcelandPdfDetails.data.zipObject

  // Check if CLRA columns should be shown (CLRA filter selected OR search active)
  const showCLRAColumns = filters.establishmentType === 'CLRA' || isSearchActive
  
  // Loading state
  const loading = importStateRegisterMappingDetails.status === 'loading' || 
      deleteStateRegisterMappingDetails.status === 'loading' || 
      updateStateRegisterDetails.status === 'loading' || 
      addStateRegisterDetails.status === 'loading' || 
      formsDetails.status === 'loading' || 
      stateRegisterQueDetails.status === 'loading'
  
  const handleChangeCompany = (event: any) => {
    const selectedCompanyId = event.target.value as string;
    updateFilter('company', selectedCompanyId);

    fetchData({ ...filters, ...getResetFields('company'), company: selectedCompanyId }, undefined, { que_id_eq: searchConfig.searchInput });

    if (selectedCompanyId) {
      fetchAssociateCompanies(selectedCompanyId);
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeAssociateCompany = (event: any) => {
    const selectedCompanyId = event.target.value as string;
    updateFilter('associateCompany', selectedCompanyId);

    fetchData({ ...filters, ...getResetFields('associateCompany'), associateCompany: selectedCompanyId });

    if (selectedCompanyId) {
      fetchLocations(selectedCompanyId);
      fetchStates();
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeStateName = (event:any) => {
    const selectedStateId = event.target.value as string;
    updateFilter('stateName', selectedStateId);

    fetchData({ ...filters, ...getResetFields('stateName'), stateName: selectedStateId });
  }

  const handleChangeLocation = (event: any) => {
    //alert(event.target.value);
    const selectedLocations = typeof event.target.value === 'string' ? [event.target.value] : event.target.value;
    updateFilter('locations', selectedLocations);

    fetchData({ ...filters, ...getResetFields('locations'), locations: selectedLocations });
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, ...getResetFields('locations'), locations: selectedLocations }, locations);
    }
  };

  
  const handleChangeYear = (event:any) => {
    const selectedYear = event.target.value as string;
    updateFilter('year', selectedYear);

    fetchData({ ...filters, ...getResetFields('year'), year: selectedYear });
  };
  
  const handleChangeMonth = (event:any) => {
    const selectedMonth = event.target.value as string;
    updateFilter('month', selectedMonth);

    fetchData({ ...filters, month: selectedMonth });
  };

  const handleChangeEstablishmentType = (event: any) => {
    const selectedType = event.target.value;
    setIsSearchActive(false);
    updateFilter('establishmentType', selectedType);
    const resetFields = getResetFields('establishmentType');
    const cleanFilters = {
      company: '',
      associateCompany: '',
      stateName: '',
      locations: [],
      year: '',
      month: '',
      employer: '',
      employerLocation: '',
      subContractor: '',
      subContractorLocation: '',
      ...resetFields,
      establishmentType: selectedType
    };

    fetchData(cleanFilters, undefined, { que_id_eq: searchConfig.searchInput });
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchCompanies();
    fetchData();
  }, []);

  // Fetch associate companies when company changes
  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
    }
  }, [filters.company]);

  // Fetch locations when associate company changes
  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      fetchStates();
    }
  }, [filters.associateCompany]);

  // Removed: Consolidated with main useEffect above

  // Removed: Consolidated with filters.company useEffect

  // Removed: Consolidated with filters.associateCompany useEffect



  useEffect(() => {
    if (stateRegisterQueDetails.status === 'succeeded') {
  //alert("this success  was hitted");
    } else if (stateRegisterQueDetails.status === 'failed') {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [stateRegisterQueDetails.status])


  // Fetch employer-related data when filters change
  useEffect(() => {
    if (filters.locations && filters.locations.length > 0) {
      fetchEmployers(filters, locations);
    }
  }, [filters.locations]);

  useEffect(() => {
    if (filters.employer) {
      fetchEmployerLocations(filters, locations);
    }
  }, [filters.employer]);

  useEffect(() => {
    if (filters.employerLocation) {
      fetchSubContractors(filters, locations);
    }
  }, [filters.employerLocation]);

  useEffect(() => {
    if (filters.subContractor) {
      fetchSubContractorLocations(filters, locations);
    }
  }, [filters.subContractor]);

  function handleChangeEmployer(event: any) {
    const selectedEmployer = event.target.value as string;
    updateFilter('employer', selectedEmployer);

    fetchData({ ...filters, ...getResetFields('employer'), employer: selectedEmployer });

    if (selectedEmployer) {
      fetchEmployerLocations({ ...filters, ...getResetFields('employer'), employer: selectedEmployer }, locations);
    }
  }

  function handleChangeEmployerLocation(event: any) {
    const selectedLocation = event.target.value as string;
    updateFilter('employerLocation', selectedLocation);

    fetchData({ ...filters, ...getResetFields('employerLocation'), employerLocation: selectedLocation });

    if (selectedLocation) {
      fetchSubContractors({ ...filters, ...getResetFields('employerLocation'), employerLocation: selectedLocation }, locations);
    }
  }

  function handleChangeSubContractor(event: any) {
    const selectedSubContractor = event.value as string;
    updateFilter('subContractor', selectedSubContractor);
    
    if (selectedSubContractor) {
      fetchSubContractorLocations({ ...filters, subContractor: selectedSubContractor }, locations);
    }
  }

  function handleChangeSubContractorLocation(event: any) {
    const selectedLocation = event.value as string;
    updateFilter('subContractorLocation', selectedLocation);
  }

  function applyMoreFilters() {
    setOpenMoreFilters(false);
    fetchData();
  }

  function clearMoreFilters() {
    setDebugForm(null);
    updateMultipleFilters({
      "subContractor": "",
      "subContractorLocation": ""
    });
    setTimeout(() => {
      setOpenMoreFilters(false);
      setAnchorEl(null);
      fetchData({...filters, subContractor: "", subContractorLocation: ""});
    }, 100);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(undefined, { page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({ rowsPerPage: newRowsPerPage, page: 0 });
    fetchData(undefined, { rowsPerPage: newRowsPerPage, page: 0 });
  };

  const onClickSortCompany = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'company_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'company_id', newSortType);
  }

  const onClickSortAssociateCompany = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'associate_company_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'associate_company_id', newSortType);
  }

  const onClickSortLocation = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'location_id', sortType: newSortType });
    fetchData(filters, undefined, {}, 'location_id', newSortType);
  }

  const onClickSortYear = () => {
    const newSortType = searchConfig.sortType === 'asc' ? 'desc' : 'asc';
    updateSearchConfig({ activeSort: 'year', sortType: newSortType });
    fetchData(filters, undefined, {}, 'year', newSortType);
  }


  // Use utility functions for lists
  const yearsList = generateYearsList();
  const monthList = MONTH_LIST;
  const EstablishmentTypesList = Object.values(ESTABLISHMENT_TYPE);

  const handleProcessRegisters = async () => {
    setDownloading(true);
    let newQueueNumbers: string[] = [];
    try {
      for (const location of filters.locations) {
        let payload: any = {};
        switch (filters.establishmentType) {
          case 'CLRA':
            const loc = locations.find((l: any) => l.location_id === location);
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: loc ? loc.location_id : undefined,
                state_id: loc && loc.location && loc.location.state ? loc.location.state.id : undefined,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType,
                company_location_mapping_id: loc ? loc.id : undefined
              }
            };
            if(filters.employer) {
              payload.state_register_que['employer_id'] = filters.employer;
            }
            if(filters.employerLocation) {
              payload.state_register_que['employer_location_id'] = filters.employerLocation;
            }
            if(filters.subContractor) {
              payload.state_register_que['sub_contractor_id'] = filters.subContractor;
            }
            if(filters.subContractorLocation) {
              payload.state_register_que['sub_contractor_location_id'] = filters.subContractorLocation;
            }
            break;
          case 'Shops':
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: location,
                state_id: filters.stateName,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType
              }
            };
            break;
          default:
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: location,
                state_id: filters.stateName,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType
              }
            };
            break;
        }
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType: filters.establishmentType }))
        if(addStateRegisterQue.fulfilled.match(resultAction)) {
          console.log('✅ Update successful:', resultAction.payload);
          if(filters.establishmentType === 'CLRA') {
            const queList = resultAction.payload.data.state_register_ques;
            const skipped_list = resultAction.payload.data.skipped_employers || [];
            if(queList && Array.isArray(queList)) {
              const queIds = queList.map((item: any) => item.que_id).filter(Boolean);
              console.log('CLRA queIds (array):', queIds);
              newQueueNumbers.push(...queIds);
            } else {
              const queId = resultAction.payload.data.que_id;
              console.log('CLRA queId (single):', queId);
              if(queId) {
                newQueueNumbers.push(queId);
              }
            }
            if(skipped_list.length > 0) {                          
              toast.warn(`${skipped_list.length} record(s) were skipped due to missing data (no employees/wage/attendance data found).`);
            } 
          } else {
            const queId = resultAction.payload.data.que_id;
            console.log('Non-CLRA queId:', queId);
            if(queId) {
              newQueueNumbers.push(queId);
            }
          }
        } else {
          const errorData: any = resultAction.payload || {};
          const skipped_list = errorData.skipped_employers || [];
          if(skipped_list.length > 0) {
            toast.warn(`${skipped_list.length} record(s) were skipped due to missing data.`);
          }
          console.error('❌ Update failed:', resultAction.payload);
          if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            toast.error(
              <div>
                {errorData.errors.map((err: string, i: number) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            );
          } else if (errorData.error) {
            toast.error(errorData.error);
          } else {
            toast.error("An error occurred while processing the registers.");
          }
          setDownloading(false);
          return;
        }
      }
      // Update the state with the new queue numbers
      SetqueueNumbers((prevQueueNumbers) => [...prevQueueNumbers, ...newQueueNumbers]);
      console.log(queueNumbers);
      // Step 3: Display the QueueNo list to the user in a popup
      if (newQueueNumbers.length > 0) {
        alert(`Queue Numbers received : \n${newQueueNumbers.join(", ")}`);
        fetchData();

      } else {
        toast.error('No Queue Numbers found.');
      }
      setDownloading(false);
    } catch (error) {
      console.error("Error processing registers:", error);
      alert("An error occurred while processing the registers.");
      setDownloading(false); // Reset the downloading state even if there's an error
    }
  };

  // Moved interfaces to utils if needed globally

  const onClickDownloadZip = (url:any) => {
    if(url) {
      downloadWithPresignedUrl(url.split('/').pop(), url)
    } else {
      toast.info("No URL found");
    }
  }

  const onclickDownloadExcel=(file:any)=>{
    console.log(file);
    const url = file.register_excel_url || '';
    if (url) {
      downloadWithPresignedUrl(url.split('/').pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  }

  const onclickDownloadPdf=(file:any)=>{
    console.log(file);
    const url = file.register_pdf_url || '';
    if (url) {
      downloadWithPresignedUrl(url.split('/').pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  }

  // Function to handle the download of all files
  // const handleFileDownloads = async (files: FileDetails[]) => {
  //   for (const file of files) {
  //    // alert(file.filePath);
  //     await onclickDownloadExcel(file);
  //   }
  // };

  const onclickDownloadBoth=(each :any)=>{
    setOpenDownloadModal(true);
    console.log(each);
    /*
    const Payload: any =  { 
      company_id: each.company_id,
      associate_company_id:  each.associate_company_id,
      location_id: each.location_id,
      state_id: each.state_id, 
      year: each.year, 
      month: each.month,
      establishment_type : each.establishment_type,
      zipType : "ExcelZip"
    }
    */
    //dispatch(getstateRegisterQueExcelandPdfDownloadDetails(Payload))
    setSelectedStateRegisterQue(each);
    const _stateRegisters = each.state_registers || [];
    const updatedStateRegisters: any = [];
    _stateRegisters.forEach((_stateRegister: any) => {
      const _registers = [...(_stateRegister.registers || [])].sort((a: any, b: any) => Number(a.id) - Number(b.id));
      if(_registers.length > 1) {
        updatedStateRegisters.push(
          ..._registers.map((register: any, index: number) => {
            return {
              ..._stateRegister,
              form_name: _stateRegister.form_name + ` Part - ${index + 1}`,
              register_excel_url: register.excel_url || '',
              register_pdf_url: register.pdf_url || ''
            }
          })
        );
      } else {
        updatedStateRegisters.push({
          ..._stateRegister,
          register_excel_url: (_registers[0] || {}).excel_url || '',
          register_pdf_url: (_registers[0] || {}).pdf_url || ''
        });
      }
    });
    setStateRegisters(updatedStateRegisters);
  }

  // Using formatDate from utils

  const handleChangeSearchInput = (event: any) => {
    updateSearchConfig({ searchInput: event.target.value });
  }

  const onClickClearSearch = () => {
    setIsSearchActive(false);
    updateSearchConfig({ searchInput: '' });
    fetchData();
  }

  const onClickSearch = () => {
    setIsSearchActive(true);
    updateSearchConfig({ page: 0 });
    fetchWithSearch(searchConfig.searchInput);
  }

  const moreFiltersSchema: any = {
    fields: [
      {
        component: componentTypes.SELECT,
        name: 'subContractor',
        label: 'Sub Contractor',
        options: employerSubContractors.map((x: any) => ({
            label: x.name,
            value: x.id
        })),
        initialValue: (debugForm || {}).subContractor,
        isMulti: false,
        isDisabled: !filters.employerLocation,
        isLoading: !employerSubContractors,
        onChange: handleChangeSubContractor.bind(this)
      },
      {
        component: componentTypes.SELECT,
        name: 'subContractorLocation',
        label: 'Sub Contractor Location',
        options: employerSubContractorsLocations.map((x: any) => ({
            label: x.location_name,
            value: x.id
        })),
        initialValue: (debugForm || {}).subContractorLocation,
        isMulti: false,
        isDisabled: !filters.subContractor,
        isLoading: !employerSubContractorsLocations,
        onChange: handleChangeSubContractorLocation.bind(this)
      }
    ]
  }

  return (
    <div style={{ backgroundColor:'#ffffff', minHeight:'100vh'}}>
      {/* Upload Modal */}
      <Modal
        open={openDownloadModal}
        onClose={() => {setDownloading(false)}}
      >
        <Box sx={styleUploadModal}>
          <Box sx={{backgroundColor:'#E2E3F8', padding:'10px', px:'20px', borderRadius:'6px', boxShadow: '0px 6px 10px #CDD2D9', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <Typography sx={{font: 'normal normal normal 32px/40px Calibri'}}>Excel & Pdf Download</Typography>
            <IconButton
              onClick={() => {setOpenDownloadModal(false);}}
            >
              <IoMdClose />
            </IconButton>
          </Box>

          <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', maxHeight: '385px', overflowY: 'scroll' }}>
              <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7',fontWeight:'600' } }}>
                  <TableRow>
                    {/* <TableCell><Checkbox/></TableCell> */}
                    <TableCell > <TableSortLabel active={searchConfig.activeSort === 'company_id'} direction={searchConfig.sortType} onClick={onClickSortCompany}>FormName</TableSortLabel></TableCell>
                    <TableCell > 
                      <TableSortLabel active={searchConfig.activeSort === 'associate_company_id'} direction={searchConfig.sortType} onClick={onClickSortAssociateCompany}>Download Excel</TableSortLabel>
                    </TableCell>
                    <TableCell >Download Pdf</TableCell>
                    <TableCell >First Generated Date</TableCell>
                    <TableCell >Last Generated Date</TableCell>
                    <TableCell >Total Generations</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>

                  {stateRegisters && stateRegisters.map((each: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      {/* <TableCell><Checkbox/></TableCell> */}
                      <TableCell >{each.form_name ? each.form_name : 'NA'}</TableCell>
                      <TableCell >
                        <Box sx={{ display: 'flex',justifyContent: "center" }}>
                          {each.register_excel_url && <Icon 
                              style={{ color: '#0654AD' }} 
                              type="button" 
                              name={'download'} 
                              text={'download'}  
                              action={() => onclickDownloadExcel(each)}
                          />}
                        </Box>
                      </TableCell>
                      
                      <TableCell >
                        <Box sx={{ display: 'flex',justifyContent: "center" }}>
                          {each.register_pdf_url && <Icon 
                              style={{ color: '#0654AD' }} 
                              type="button" 
                              name={'download'} 
                              text={'download'}  
                              action={() => onclickDownloadPdf(each)}
                          />}
                        </Box>
                      </TableCell>
                      <TableCell >{formatDate(each.created_at)}</TableCell>
                      <TableCell >{formatDate(each.updated_at)}</TableCell>
                      <TableCell >{each.count}</TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          <Box sx={{display:'flex', padding:'20px', borderTop:'1px solid #6F6F6F',justifyContent:'flex-end', alignItems:'center', mt:4}}>
            <Button variant='contained' sx={{backgroundColor:'#707070',mr:10}} onClick={() => {onClickDownloadZip(selectedStateRegisterQue.register_url)}}>Download Excel Zip</Button>
            <Button variant='contained' sx={{backgroundColor:'#707070',mr:10}} onClick={() => {onClickDownloadZip(selectedStateRegisterQue.pdf_register_url)}}>Download PDF Zip</Button>
            <Button variant='contained' sx={{backgroundColor:'#707070'}} onClick={() => {setOpenDownloadModal(false)}}>Cancel</Button>
          </Box>
        </Box>
      </Modal>
      {loading ? <PageLoader>Loading...</PageLoader> : 
      <div>
        {downloading ? <PageLoader>Processing Registers...</PageLoader> :

        <div style={{paddingBottom:'100px'}}>

            {/* Filter Box*/}
            <Box sx={{paddingX: '20px', paddingY:'10px',}}>
              <div style={{backgroundColor:'#E2E3F8', padding:'20px', borderRadius:'6px', boxShadow: '0px 6px 10px #CDD2D9'}}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', marginTop: '10px' }}>
                    <h5 style={{ font: 'normal normal normal 32px/40px Calibri' }}>Que Registers</h5>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', marginTop: '10px' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Establishment Type</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.establishmentType}
                          onChange={handleChangeEstablishmentType}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Type
                          </MenuItem>
                          {EstablishmentTypesList && EstablishmentTypesList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>
                    <Box sx={{ width: '100%', textAlign: 'right'}}>
                      <Typography mb={1}>&nbsp;</Typography>
                      <FormControl sx={{ width: '60%', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <InputLabel htmlFor="outlined-adornment-search">Search Que No</InputLabel>
                        <OutlinedInput
                          value={searchConfig.searchInput}
                          onChange={handleChangeSearchInput}
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          id="outlined-adornment-search"
                          type='text'

                          endAdornment={
                            <InputAdornment position="end">
                              {searchConfig.searchInput &&
                                <IconButton
                                  onClick={onClickClearSearch}
                                  edge="end"
                                >
                                  <IoMdClose />
                                </IconButton>
                              }
                              <IconButton
                                onClick={onClickSearch}
                                edge="end"
                              >
                                <IoMdSearch />
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Search"
                        />
                      </FormControl>
                    </Box>
                  </div>
               
                  <div style={{ display: 'flex' }}>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Company</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          value={filters.company}
                          displayEmpty
                          disabled={!filters.establishmentType}
                          onChange={handleChangeCompany}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 230,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Company
                          </MenuItem>
                          {companies && companies.map((each: any) => {
                            return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                          })}
                        </MSelect>
                      </FormControl>
                    </Box>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Associate Company</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.associateCompany}
                          disabled={!filters.company}
                          onChange={handleChangeAssociateCompany}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 230,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Associate Company
                          </MenuItem>
                          {associateCompanies && associateCompanies.map((each: any) => {
                            return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                          })}
                        </MSelect>
                      </FormControl>
                    </Box>

                    {filters.establishmentType !== "CLRA" && 
                    <>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>State</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.stateName}
                            disabled={!filters.associateCompany}
                            onChange={handleChangeStateName}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select State
                            </MenuItem>
                            {states && states.map((each: any) => {
                              return <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.locations}
                            multiple // Enable multiple selection
                            disabled={!filters.stateName}
                            onChange={handleChangeLocation}
                            input={<OutlinedInput />}
                            renderValue={(filters.locations || []).length > 0 ?(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value: string) => {
                                  // Find the selected location by its ID
                                  const selectedLocation = locations.find((loc: any) => loc.location_id === value);
                                  // Display the location name if found
                                  return selectedLocation ? <Chip key={value} label={selectedLocation.location.name} /> : null;
                                })}
                              </Box>
                            ) : () => <>Select Location</>}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value={[]}>
                              Select Location
                            </MenuItem>
                            {locations && locations.map((each: any) => {
                              const { id, name, code, state, city }: any = each.location || {};
                              if (state.id === filters.stateName) {
                                return <MenuItem key={each.location_id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.location_id}>{`${name} (${state.code}-${city.code}-${code})`}</MenuItem>
                              }
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>
                    </>
                    }


                    {filters.establishmentType === "CLRA" && <>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Company Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.locations}
                            multiple={false}
                            disabled={!filters.associateCompany}
                            onChange={handleChangeLocation}
                            input={<OutlinedInput />}
                            renderValue={(filters.locations || []).length > 0 ? (selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value: string) => {
                                  // Find the selected location by its ID
                                  const selectedLocation = locations.find((loc: any) => loc.location_id === value);
                                  // Display the location name if found
                                  return selectedLocation ? selectedLocation.location.name : null;
                                })}
                              </Box>
                            ) : () => <>Select Company Location</>}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value={[]}>
                              Select Company Location
                            </MenuItem>
                            {locations && locations.map((each: any) => {
                              const { id, name, code, state, city }: any = each.location || {};
                              return <MenuItem key={each.location_id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.location_id}>{`${name} (${state.code}-${city.code}-${code})`}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Employer</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.employer}
                            disabled={filters.locations && filters.locations.length === 0}
                            onChange={handleChangeEmployer}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 230,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select Employer
                            </MenuItem>
                            {employers && employers.map((each: any) => {
                              return <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.name}</MenuItem>
                            })}
                          </MSelect>
                        </FormControl>
                      </Box>

                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Typography mb={1}>Employer Location</Typography>
                        <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                          <MSelect
                            sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                            displayEmpty
                            value={filters.employerLocation}
                            disabled={!filters.employer}
                            onChange={handleChangeEmployerLocation}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 200,
                                  width: 150,
                                  marginTop: "3px"
                                }
                              }
                            }}
                          >
                            <MenuItem disabled sx={{ display: 'none' }} value="">
                              Select Employer Location
                            </MenuItem>
                            {employerLocations && employerLocations.map((each: any) =>
                              <MenuItem key={each.id} sx={{ width: '240px', whiteSpace: 'initial' }} value={each.id}>{each.location_name}</MenuItem>
                            )}
                          </MSelect>
                        </FormControl>
                      </Box>
                    </>}

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Year</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.year}
                          disabled={!filters.locations.length}
                          onChange={handleChangeYear}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Year
                          </MenuItem>
                          {yearsList && yearsList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>

                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Typography mb={1}>Month</Typography>
                      <FormControl sx={{ width: '100%', maxWidth: '200px', backgroundColor: '#ffffff', borderRadius: '5px' }} size="small">
                        <MSelect
                          sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                          displayEmpty
                          value={filters.month}
                          disabled={!filters.year}
                          onChange={handleChangeMonth}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                width: 150,
                                marginTop: "3px"
                              }
                            }
                          }}
                        >
                          <MenuItem disabled sx={{ display: 'none' }} value="">
                            Select Month
                          </MenuItem>
                          {monthList && monthList.map((each: any) =>
                            <MenuItem sx={{ width: '240px', whiteSpace: 'initial' }} value={each}>{each}</MenuItem>
                          )}
                        </MSelect>
                      </FormControl>
                    </Box>

                    {filters.establishmentType === 'CLRA' && <>
                      <div className="d-flex align-items-end me-3">
                        <div className="d-flex flex-column">
                            <button type="submit" className="btn btn-primary d-flex align-items-center"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if(!filters.employer || !filters.employerLocation){
                                      return toast.error("Please select Employer and Employer Location");
                                    }
                                    setAnchorEl(e.currentTarget);
                                    setOpenMoreFilters(true);
                                }}
                                aria-describedby={openMoreFilters ? 'more-filters-popover' : undefined}
                                style={{width: '136px'}}>
                                <span className="ms-2">More Filters</span>
                            </button>
                        </div>
                      </div>

                      
                      <Popover 
                        id="more-filters-popover"
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        open={openMoreFilters}
                      >
                        <div className="px-3 py-2" style={{width: '500px'}}>
                          <div className="bold mb-10 d-flex justify-content-between" style={{fontSize: '18px', fontWeight: 600, lineHeight: '20px', width: '100%', borderBottom: '1px solid #F2F2F2'}}>
                            <div style={{display: 'flex', alignItems: 'center'}}>Additional Filters</div>
                            <div><IconButton onClick={() => {
                              setOpenMoreFilters(false);
                              setAnchorEl(null);
                            }}><IoMdClose /></IconButton></div>
                          </div>
                          <div className="d-flex justify-content-start">
                            <div className="col-12">
                              <form>
                                <div className="row mt-3">
                                  <div className="col-12">
                                    <FormRenderer FormTemplate={FormTemplate}
                                      initialValues={{hideButtons: true, ...debugForm}}
                                      componentMapper={ComponentMapper}
                                      schema={moreFiltersSchema}
                                      debug={(e: any) => { setDebugForm(e.values); }}
                                    />
                                  </div>
                                </div>
                              </form>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-4 mb-2">
                            <Button variant="outlined" onClick={clearMoreFilters} className="px-4">Clear</Button>
                            <Button variant="contained" onClick={applyMoreFilters} className="px-4">Filter</Button>
                          </div>

                        </div>
                      </Popover>
                    </>}
                    
                  </div>
              </div>
            </Box>
            {/* Status Box */}
            {showDashboardDetails && 
              <Box sx={{paddingX: '20px', color:'#F3F4F8', mt:2, }}>
                <Box sx={{display:'flex', justifyContent:'space-between', width:'100%', padding:'15px', background:'#EFEBFE 0% 0% no-repeat padding-box', boxShadow:'0px 6px 10px #00000029', borderRadius:'8px'}}>
                { filters.establishmentType !== 'CLRA' &&
                  hasUserAccess(USER_PRIVILEGES.REGISTER_GENERATION_QUE) &&
                  <Button sx={{mt:2, width:'90%'}} variant='outlined' onClick={handleProcessRegisters} disabled={ !filters.company || !filters.associateCompany || !filters.stateName || !filters.locations.length || !filters.year || !filters.month || !filters.establishmentType }>
                  Generate Registers
                  </Button>
                }
                { filters.establishmentType === 'CLRA' &&
                  hasUserAccess(USER_PRIVILEGES.REGISTER_GENERATION_QUE) &&
                  <Button sx={{mt:2, width:'90%'}} variant='outlined' onClick={handleProcessRegisters} disabled={ !filters.company || !filters.associateCompany || !filters.year || !filters.month || !filters.establishmentType }>
                  Generate Registers
                  </Button>
                }
                </Box>
              </Box>
            }
            <Box sx={{ paddingX: '20px' }}>
            {
              stateRegisterQue && stateRegisterQue.length <= 0 ?
                <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant='h5'>No Records Found</Typography>
                </Box>
                :
                <>
                  <TableContainer sx={{ border: '1px solid #e6e6e6', marginTop: '10px', maxHeight: '385px', overflowY: 'scroll' }}>
                    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="sticky table">
                      <TableHead sx={{ '.MuiTableCell-root': { backgroundColor: '#E7EEF7',fontWeight:'600' } }}>
                        <TableRow>
                          {/* <TableCell><Checkbox/></TableCell> */}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'company_id'} direction={searchConfig.sortType} onClick={onClickSortCompany}>Company</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'associate_company_id'} direction={searchConfig.sortType} onClick={onClickSortAssociateCompany}>AssociateCompany</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'state_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>State</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Location</TableSortLabel></TableCell>
                          {showCLRAColumns && (
                            <>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employer_location.location_name'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Employer</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'employer_location.id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Employer Location</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'sub_contractor'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Sub Contractor</TableSortLabel></TableCell>
                              <TableCell > <TableSortLabel active={searchConfig.activeSort === 'sub_contractor_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Sub Contractor Location</TableSortLabel></TableCell>
                            </>
                          )}
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Year</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'location_id'} direction={searchConfig.sortType} onClick={onClickSortLocation}>Month</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'year'} direction={searchConfig.sortType} onClick={onClickSortYear}>Que Id</TableSortLabel></TableCell>
                          <TableCell > <TableSortLabel active={searchConfig.activeSort === 'year'} direction={searchConfig.sortType} onClick={onClickSortYear}>Que Status</TableSortLabel></TableCell>
                          <TableCell >Download</TableCell>
                          <TableCell >Created Date</TableCell>
                          <TableCell >Last Updated</TableCell>
                          {/* <TableCell >Completed Date</TableCell> */}

                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stateRegisterQue && stateRegisterQue.map((each: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            {/* <TableCell><Checkbox/></TableCell> */}
                            <TableCell >{each.company && each.company.name ? each.company.name : 'NA'}</TableCell>
                            <TableCell >{each.associate_company && each.associate_company.name ? each.associate_company.name : 'NA'}</TableCell>
                            <TableCell >{each.state && each.state.name ? each.state.name : 'NA'}</TableCell>
                            <TableCell >{each.location && each.location.name ? each.location.name : 'NA'}</TableCell>

                            {showCLRAColumns && (
                              <>
                                <TableCell >{each.employer && each.employer.name ? each.employer.name : 'NA'}</TableCell>
                                <TableCell >{each.employer_location && each.employer_location.location_name ? each.employer_location.location_name : 'NA'}</TableCell>
                                <TableCell >{each.sub_contractor && each.sub_contractor.name ? each.sub_contractor.name : 'NA'}</TableCell>
                                <TableCell >{each.sub_contractor_location && each.sub_contractor_location.location_name ? each.sub_contractor_location.location_name : 'NA'}</TableCell>
                              </>
                            )}

                            <TableCell >{each.year && each.year ? each.year : 'NA'}</TableCell>
                            <TableCell >{each.month && each.month ? each.month : 'NA'}</TableCell>
                            <TableCell >{each.que_id && each.que_id ? each.que_id : 'NA'}</TableCell>
                            <TableCell >{each.que_status ? (QUE_STATUS_DISPLAY[each.que_status] || each.que_status) : 'NA'}</TableCell>
                            <TableCell >
                            <Box sx={{ display: 'flex',justifyContent: "center" }}>
                              {each.que_status === 'pdf_processing_partially_completed' || each.que_status === 'pdf_processing_completed' ? (
                                <Icon 
                                    style={{ color: '#0654AD', opacity: 1, pointerEvents: 'auto' }} 
                                    type="button" 
                                    name={'download'} 
                                    text={'download'} 
                                    action={() => onclickDownloadBoth(each)} 
                                />
                              ): (
                                <Icon
                                  style={{ color: '#0654AD', opacity: 0.5, pointerEvents: 'none' }}
                                  name={'download'}
                                  text={'download'}
                                />
                              )
                              }
                            </Box>
                            </TableCell>
                            <TableCell >{each.created_at && formatDate(each.created_at) ? formatDate(each.created_at) : 'NA'}</TableCell>
                            <TableCell >{each.completed_date && formatDate(each.completed_date) ? formatDate(each.completed_date) : 'NA'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-toolbar': {
                        backgroundColor: '#EFEBFE',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'flex-end'
                      },
                      '.MuiTablePagination-displayedRows': {
                        margin: '0',
                      },
                      '.MuiTablePagination-selectLabel': {
                        margin: '0',
                      },
                      '.MuiTablePagination-spacer': {
                        display: 'none '
                      },
                      '.MuiTablePagination-input': {
                        marginRight: 'auto'
                      },
                    }}

                    labelRowsPerPage='Show'
                    labelDisplayedRows={(page) =>
                      `Showing ${page.from}-${page.to === -1 ? page.count : page.to} of ${page.count}`
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={stateRegisterQueCount}
                    rowsPerPage={searchConfig.rowsPerPage}
                    page={searchConfig.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
            }
          </Box>
        </div>
        }
     </div>
    }
    </div>
  )
}
export default InputsDashboard